import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Switch,
  Grid,
  CircularProgress,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import toast from 'react-hot-toast'
import api from '../services/api'

interface Role {
  id: string
  name: string
  description: string
  permissions: Record<string, boolean>
  permissions_count: number
  is_active: boolean
}

// Roles seeded by RBACService.DEFAULT_ROLES on the backend — cannot be deleted from the UI.
const DEFAULT_ROLE_NAMES = [
  'Super Administrator',
  'Business Administrator',
  'Purchase Administrator',
  'Sales Administrator',
  'Billing Administrator',
  'Dispatch Administrator',
  'IT Administrator',
  'Viewer',
]

// Heuristic matching write-type permissions, for the at-a-glance anomaly badge.
const WRITE_PERMISSION_PATTERN = /create|update|delete|write|manage|approve|process|pay|verify|receive|send|confirm|cancel/i

const countWritePermissions = (permissions: Record<string, boolean> | undefined): number => {
  if (!permissions) return 0
  return Object.entries(permissions).filter(([key, enabled]) => enabled && WRITE_PERMISSION_PATTERN.test(key)).length
}

interface PermissionGridProps {
  categories: Record<string, Array<{ key: string; label: string }>>
  selected: Record<string, boolean>
  onToggle: (key: string) => void
}

const PermissionGrid: React.FC<PermissionGridProps> = ({ categories, selected, onToggle }) => (
  <>
    {Object.entries(categories).map(([category, perms]) => (
      <Paper key={category} elevation={0} sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{
            pb: 1,
            mb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            textTransform: 'uppercase',
            fontWeight: 700,
            color: 'primary.main',
            display: 'flex',
            justifyContent: 'space-between'
          }}
        >
          {category.replace(/_/g, ' ')} Management
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {perms.length} Permissions
          </Typography>
        </Typography>

        <Grid container spacing={2}>
          {perms.map((perm) => (
            <Grid item xs={12} sm={6} md={4} key={perm.key}>
              <Box sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: selected[perm.key] ? 'primary.light' : 'transparent',
                bgcolor: selected[perm.key] ? 'primary.lighter' : 'background.paper',
                display: 'flex',
                alignItems: 'flex-start',
                transition: 'all 0.2s'
              }}>
                <Switch
                  size="small"
                  checked={!!selected[perm.key]}
                  onChange={() => onToggle(perm.key)}
                  color="primary"
                  sx={{ mt: -0.5 }}
                />
                <Box sx={{ ml: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    {perm.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block', mt: 0.5 }}>
                    {perm.key}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    ))}
  </>
)

const RolesPage: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [allPermissions, setAllPermissions] = useState<Record<string, string>>({})
  const [searchQuery, setSearchQuery] = useState('')

  // Temporary state for editing permissions
  const [editingPermissions, setEditingPermissions] = useState<Record<string, boolean>>({})

  // Create-role dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newRoleName, setNewRoleName] = useState('')
  const [newRoleDescription, setNewRoleDescription] = useState('')
  const [newRolePermissions, setNewRolePermissions] = useState<Record<string, boolean>>({})

  // Delete-role dialog state
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    loadRoles()
    loadPermissions()
  }, [])

  const loadRoles = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await api.get('/admin/system/roles')
      setRoles(response.data)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load roles')
    } finally {
      setLoading(false)
    }
  }

  const loadPermissions = async () => {
    try {
      const response = await api.get('/admin/system/permissions')
      setAllPermissions(response.data.permissions)
    } catch (err) {
      console.error('Failed to load permissions:', err)
    }
  }

  const handleEditRole = (role: Role) => {
    setSelectedRole(role)
    setEditingPermissions(role.permissions || {})
    setEditDialogOpen(true)
  }

  const handleTogglePermission = (permissionKey: string) => {
    setEditingPermissions(prev => ({
      ...prev,
      [permissionKey]: !prev[permissionKey]
    }))
  }

  const handleSaveChanges = async () => {
    if (!selectedRole) return

    try {
      setSaving(true)
      // Convert dictionary back to list of active permission keys for the backend
      const activePermissions = Object.keys(editingPermissions).filter(key => editingPermissions[key])

      await api.post(`/admin/system/roles/${selectedRole.id}/permissions`, activePermissions)

      toast.success(`Permissions updated for ${selectedRole.name}`)
      setEditDialogOpen(false)
      loadRoles() // Refresh the list
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to update permissions')
    } finally {
      setSaving(false)
    }
  }

  const handleOpenCreateDialog = () => {
    setNewRoleName('')
    setNewRoleDescription('')
    setNewRolePermissions({})
    setSearchQuery('')
    setCreateDialogOpen(true)
  }

  const handleToggleNewRolePermission = (permissionKey: string) => {
    setNewRolePermissions(prev => ({
      ...prev,
      [permissionKey]: !prev[permissionKey]
    }))
  }

  const handleCreateRole = async () => {
    if (!newRoleName.trim()) {
      toast.error('Role name is required')
      return
    }

    try {
      setCreating(true)
      const permissions = Object.keys(newRolePermissions).filter(key => newRolePermissions[key])
      await api.post('/admin/system/roles', {
        name: newRoleName.trim(),
        description: newRoleDescription.trim(),
        permissions,
      })
      toast.success(`Role "${newRoleName.trim()}" created`)
      setCreateDialogOpen(false)
      loadRoles()
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to create role')
    } finally {
      setCreating(false)
    }
  }

  const handleDeleteRole = async () => {
    if (!roleToDelete) return

    try {
      setDeleting(true)
      await api.delete(`/admin/system/roles/${roleToDelete.id}`)
      toast.success(`Role "${roleToDelete.name}" deleted`)
      setRoleToDelete(null)
      loadRoles()
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to delete role')
    } finally {
      setDeleting(false)
    }
  }

  const groupedPermissions = useMemo(() => {
    const grouped: Record<string, Array<{ key: string; label: string }>> = {}

    Object.entries(allPermissions).forEach(([key, label]) => {
      const category = key.split('.')[0] || 'other'
      if (!grouped[category]) {
        grouped[category] = []
      }
      grouped[category].push({ key, label })
    })

    return grouped
  }, [allPermissions])

  const filteredCategories = useMemo(() => {
    if (!searchQuery) return groupedPermissions

    const filtered: Record<string, Array<{ key: string; label: string }>> = {}
    const query = searchQuery.toLowerCase()

    Object.entries(groupedPermissions).forEach(([category, perms]) => {
      const matchedPerms = perms.filter(p =>
        p.key.toLowerCase().includes(query) ||
        p.label.toLowerCase().includes(query)
      )

      if (matchedPerms.length > 0 || category.toLowerCase().includes(query)) {
        filtered[category] = matchedPerms.length > 0 ? matchedPerms : perms
      }
    })

    return filtered
  }, [groupedPermissions, searchQuery])

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Enterprise Role Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage granular access control and security policies across your organization
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadRoles}
            disabled={loading}
          >
            Refresh Data
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            New Role
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Total Roles
              </Typography>
              <Typography variant="h3" sx={{ mt: 1, fontWeight: 700 }}>
                {roles.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Total Permissions
              </Typography>
              <Typography variant="h3" sx={{ mt: 1, fontWeight: 700 }}>
                {Object.keys(allPermissions).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Roles List */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Role Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Purpose & Description</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Capabilities</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Write Access</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Management</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">Fetching role configurations...</Typography>
                  </TableCell>
                </TableRow>
              ) : roles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Typography variant="body1" color="text.secondary">No roles registered in the database</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                roles.map((role) => (
                  <TableRow key={role.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                          p: 1,
                          borderRadius: 2,
                          bgcolor: 'primary.lighter',
                          color: 'primary.main',
                          display: 'flex'
                        }}>
                          <SecurityIcon fontSize="small" />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {role.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {role.id.substring(0, 8)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography variant="body2" color="text.secondary">
                        {role.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${role.permissions_count} Active Policies`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Permissions matching create/update/delete/manage/approve/pay/verify/etc.">
                        <Chip
                          label={countWritePermissions(role.permissions)}
                          size="small"
                          color={countWritePermissions(role.permissions) > 0 ? 'warning' : 'default'}
                          variant="outlined"
                          sx={{ fontWeight: 500 }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={role.is_active ? 'Operational' : 'Disabled'}
                        size="small"
                        color={role.is_active ? 'success' : 'error'}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => handleEditRole(role)}
                          sx={{ fontWeight: 600 }}
                        >
                          Manage Access
                        </Button>
                        <Tooltip title={DEFAULT_ROLE_NAMES.includes(role.name) ? 'Default system roles cannot be deleted' : 'Delete role'}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={DEFAULT_ROLE_NAMES.includes(role.name)}
                              onClick={() => setRoleToDelete(role)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Permission Management Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !saving && setEditDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Edit Access Policies: {selectedRole?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Toggle granular permissions to customize this role
            </Typography>
          </Box>
          <IconButton onClick={() => setEditDialogOpen(false)} disabled={saving}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Box sx={{ px: 3, mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search permissions by name, key or category..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              sx: { borderRadius: 2 }
            }}
          />
        </Box>

        <DialogContent dividers sx={{ p: 3, bgcolor: 'grey.50' }}>
          <PermissionGrid categories={filteredCategories} selected={editingPermissions} onToggle={handleTogglePermission} />
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            disabled={saving}
            sx={{ fontWeight: 600 }}
          >
            Discard Changes
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveChanges}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            sx={{ fontWeight: 600, px: 4 }}
          >
            {saving ? 'Synchronizing...' : 'Update Database Roles'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Role Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => !creating && setCreateDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Create New Role
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Define a name, description and the permissions this role grants
            </Typography>
          </Box>
          <IconButton onClick={() => setCreateDialogOpen(false)} disabled={creating}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Box sx={{ px: 3, mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Role Name"
                size="small"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField
                fullWidth
                label="Description"
                size="small"
                value={newRoleDescription}
                onChange={(e) => setNewRoleDescription(e.target.value)}
              />
            </Grid>
          </Grid>
          <TextField
            fullWidth
            placeholder="Search permissions by name, key or category..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              sx: { borderRadius: 2 }
            }}
          />
        </Box>

        <DialogContent dividers sx={{ p: 3, bgcolor: 'grey.50' }}>
          <PermissionGrid categories={filteredCategories} selected={newRolePermissions} onToggle={handleToggleNewRolePermission} />
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => setCreateDialogOpen(false)}
            disabled={creating}
            sx={{ fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateRole}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={20} /> : <AddIcon />}
            sx={{ fontWeight: 600, px: 4 }}
          >
            {creating ? 'Creating...' : 'Create Role'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Role Confirmation */}
      <Dialog
        open={!!roleToDelete}
        onClose={() => !deleting && setRoleToDelete(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Role</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete <strong>{roleToDelete?.name}</strong>? This cannot be undone.
            If any admin users currently hold this role, deletion will be blocked.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={() => setRoleToDelete(null)} disabled={deleting} sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteRole}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
            sx={{ fontWeight: 600, px: 4 }}
          >
            {deleting ? 'Deleting...' : 'Delete Role'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default RolesPage
